import { AnimatePresence, motion } from 'motion/react';
import { Button } from '../design-system/Button';
import { suppressDataDetectors } from '../design-system';
import { SkipBolt } from './SkipBolt';
import type { IframePoolOrderItem } from '../api';
import type { SellState } from './WinnerStep';
import { RevealBottomPanel } from './RevealBottomPanel';
import { SleekNextPackPanel } from './sleek/SleekNextPackPanel';
import { SleekRevealBottomPanel } from './sleek/SleekRevealBottomPanel';
import { SleekRarityMarks } from './sleek/SleekRarityMarks';
import { SleekCardTitle } from './sleek/SleekCardTitle';
import { useIsSleek } from '../theme';
import { DemoBottomPanel } from './DemoBottomPanel';
import { BuybackRippleWaves } from './BuybackRippleWaves';
import { WinnerBadgeBackdrop } from './WinnerBadgeBackdrop';
import { WinnerCard } from './WinnerCard';
import { glowColorFor, isRarePlus } from './glowColorFor';
import { RevealStage } from './RevealStage';
import {
  useRevealSequence,
  ROLL_START_DELAY_MS,
} from './useRevealSequence';
import { useDelayedFlag } from './useDelayedFlag';
import { useFitText } from './useFitText';
import { useViewportHeight } from './useViewportHeight';
import { CARD_FRAME_STYLE, stageReserves } from './stageLayout';

// Title typography — `useFitText` shrinks the size for long names so they
// collapse to ≤ TITLE_MAX_LINES on any width; names still too long at
// TITLE_MIN_PX are ellipsised by the `-webkit-line-clamp` backstop inside
// the heading. Operators can retune the budget via the WinnerStepTitle
// `maxLines` knob, so TITLE_MAX_LINES is the default, not a hard cap.
// Tighter line-height per the design so three lines stay compact.
const TITLE_MAX_PX = 52;
const TITLE_MIN_PX = 22;
const TITLE_MAX_LINES = 3;
const TITLE_LINE_HEIGHT = 1.1;
// On SHORT viewports the 52px max would dwarf the card, so the cap also
// scales with viewport height: the full 52px from 1000px tall
// (52 / 1000 = 0.052), smaller below. Phones (~600–850px in a host app's
// webview) land below that on purpose: the top band reserves 3 lines at this
// cap, so a smaller cap is what leaves the card room to grow. Mirrors the
// svh curve of STAGE_TOP_RESERVE — keep the two in sync (see stageLayout.ts).
const TITLE_MAX_SVH_FRACTION = 52 / 1000;

export interface WinnerContentProps {
  /** The card to celebrate this episode. */
  item: IframePoolOrderItem;
  /** Pedestal the card stands on. */
  pedestalSrc: string;
  /** Buyback offer for the bottom panel (null → only Create Shipping). */
  bestOffer: IframePoolOrderItem['bestActiveOffer'];
  sellState: SellState;
  /** Returning view — paint final state with no entrance choreography. */
  instant: boolean;
  /** Post-shuffle episode — title + card appear instantly (the reel just
   *  delivered the card); the title slides DOWN into place and only the
   *  bottom (FMV → Buyback CTA) cascades. The full slow reveal (typewriter
   *  title) is reserved for the first, fresh pull. */
  quickHeader?: boolean;
  /** When `true` the title + bottom fly away (the next-pack shuffle is
   *  taking over the screen). */
  leaving: boolean;
  onAcceptBuyback: () => void;
  onOpenNextPack: () => void;
  /** Label of the forward CTA — "Buy another pack for {price}" when the
   *  tap buys a fresh pack, "Open Next Pack" when it advances to an
   *  already-paid sealed pack (WinnerStep owns the distinction). */
  nextPackCta: string;
  /** The forward CTA closes the reveal ("Done") — see RevealBottomPanelProps. */
  nextPackIsDone?: boolean;
  /** Sleek's Vault — see RevealBottomPanelProps. */
  onVault?: () => void;
  /** Sleek's self-closing last pack — see RevealBottomPanelProps. */
  onResultShown?: () => void;
  onCreateShipping: () => void;
  showCreateShipping: boolean;
  /** Where the card is: on this component's own pedestal (`card`, the
   *  default), or already on screen underneath, settled by the opening
   *  scene (`scene`) — the middle then stays empty so the title and the
   *  bottom panel arrive around that card instead of replacing it. */
  cardSlot?: 'card' | 'scene';
  /** Free demo rip — the bottom reads the same (FMV, primary, secondary),
   *  but nothing is sold: see DemoBottomPanel. */
  demo?: boolean;
  /** Demo secondary CTA ("Pull for Real") — leaves for the real pack. */
  onPullForReal?: () => void;
}

/**
 * One winner "episode" — badge backdrop, title, card on pedestal, and the
 * bottom buyback panel. Owns the reveal cascade (`useRevealSequence`), so
 * MOUNTING a fresh instance (the parent keys it per episode) replays the
 * reveal.
 *
 * Vertical layout is locked via `stageLayout`: a reserved top (title) and
 * bottom (panel) region with the card in the flexible middle. So the card
 * never moves when the bottom swaps offer↔resolved, and it sits at the
 * exact position the reel uses — the reel→winner crossfade has no jump.
 * The card is the flex `grow` item (`min-h-0`), so it shrinks on short
 * screens instead of overflowing (mobile-safe).
 */
export function WinnerContent({
  item,
  pedestalSrc,
  bestOffer,
  sellState,
  instant,
  quickHeader = false,
  leaving,
  onAcceptBuyback,
  onOpenNextPack,
  nextPackCta,
  nextPackIsDone = false,
  onVault,
  onResultShown,
  onCreateShipping,
  showCreateShipping,
  cardSlot = 'card',
  demo = false,
  onPullForReal,
}: WinnerContentProps) {
  // Sleek swaps the title's place, the pedestal and the bottom panel's markup
  // (partner mockup); the reveal choreography and every handler stay shared.
  const isSleek = useIsSleek();
  // Same bands in the winner, the reel and the opener scene (see stageLayout).
  const reserves = stageReserves(isSleek);
  // The backend sends a badge for every tier; Common / Uncommon are treated
  // as badge-less here so their reveal stays neutral (no backdrop, no
  // 'badge' beat, no comet) — the celebration is Rare+ only.
  const rawBadgeName = item.badge?.name ?? null;
  const badgeName = isRarePlus(rawBadgeName) ? rawBadgeName : null;

  // The CARD paints instantly on a returning view OR a post-shuffle
  // episode (the reel just delivered it); on a fresh pull it lifts in.
  // (The title always drops in via its own motion — no typewriter.)
  const headerInstant = instant || quickHeader;
  const BottomPanel = isSleek ? SleekRevealBottomPanel : RevealBottomPanel;
  // Sleek (partner mockup) stacks title + FMV right under the card and pins
  // the actions to the bottom; the demo panel keeps the shared layout.
  const isSleekBand = isSleek && !demo;

  const { visible } = useRevealSequence({
    hasBadge: badgeName !== null,
    instant,
    quickHeader,
  });


  // Two-tact appearance for any block that hosts an odometer: the row
  // fades in showing zeroes, then ROLL_START_DELAY_MS later the digits
  // roll. Keeps every FMV / Buyback beat reading as "land → roll".
  const fmvRoll = useDelayedFlag(visible('fmv'), ROLL_START_DELAY_MS);
  const ctaRoll = useDelayedFlag(visible('cta'), ROLL_START_DELAY_MS);

  // Auto-shrink long titles to the line budget on any width (the hook reads
  // the budget back off the clamped span's resolved clamp). The max size is
  // additionally capped by the viewport height so short viewports don't
  // open with a banner-sized heading (see TITLE_MAX_SVH_FRACTION).
  const viewportHeight = useViewportHeight();
  const titleMaxPx = Math.max(
    TITLE_MIN_PX,
    Math.min(TITLE_MAX_PX, Math.round(viewportHeight * TITLE_MAX_SVH_FRACTION)),
  );
  const { ref: titleRef, fontPx: titleFontPx } = useFitText({
    text: item.title,
    maxPx: titleMaxPx,
    minPx: TITLE_MIN_PX,
    maxLines: TITLE_MAX_LINES,
    lineHeight: TITLE_LINE_HEIGHT,
  });

  return (
    <motion.div
      initial={{ opacity: instant ? 1 : 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.3, ease: 'easeOut' } }}
      transition={{ duration: instant ? 0 : 0.4, ease: 'easeOut' }}
      className="absolute inset-0 flex flex-col items-center"
    >
      {/* Huge outline badge name in the background (e.g. "GRAIL")
          + central bloom. Only when there is a badge — and only behind our
          own card: a library mechanic leaves its card standing in the scene
          (`cardSlot === 'scene'`), and the word printed across it belongs to
          a screen that drew the card itself. */}
      {badgeName && cardSlot !== 'scene' && !isSleek && (
        <WinnerBadgeBackdrop
          badge={badgeName}
          visible={visible('badge')}
          instant={instant}
        />
      )}

      {/* `pt-16` clears the controls floating in the top corners (BalancePill
          left, Exit right: top-4 + h-9 = 52px) plus a 12px gap, so the title
          gets a full-width row instead of a narrow column threaded between
          them. Trading ~52px of vertical for ~90px of horizontal is a win
          here: a casino iframe is ~400px wide but tall, and every line the
          title stops wrapping to gives most of that height straight back.
          On desktop it sits lower still — wider viewports leave more room
          above the card and a flush-to-top heading reads as unbalanced. */}
      <div
        className="w-full shrink-0 pt-16 @[900px]:[@media(min-height:700px)]:pt-25 pb-2"
        style={{ minHeight: reserves.top }}
      >
        <AnimatePresence>
          {!leaving && !isSleek && (
            <motion.div
              key="winner-title"
              data-co-component="WinnerStepTitle"
              className="w-full flex flex-nowrap justify-center items-start gap-x-2 px-6"
              // Title DROPS in from above on every live reveal (first pull
              // included — no typewriter), mirroring the way it flies up on
              // exit. A returning view (`instant`) paints it in place.
              initial={instant ? false : { y: -72, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              exit={{
                y: -72,
                opacity: 0,
                transition: { duration: 0.4, ease: [0.4, 0, 1, 1] },
              }}
            >
              {/* Gutters default to 0: the title now sits BELOW the corner
                  controls rather than beside them, so it needs no side
                  clearance. Kept (mirrored, so any width leaves the heading
                  dead-centre) because operator themes may already override
                  `--co-WinnerStepTitle-exitSpacerWidth`. */}
              <span
                aria-hidden
                className="block shrink-0 self-start"
                style={{ width: 'var(--co-WinnerStepTitle-exitSpacerWidth, 0px)' }}
              />
              <h2
                aria-label={item.title}
                className="flex-auto min-w-0 font-bold uppercase text-center max-w-190 tracking-[-0.4px] [word-break:break-word]"
                style={{
                  color: 'var(--co-WinnerStepTitle-color, #FFFFFF)',
                  fontFamily:
                    'var(--co-WinnerStepTitle-font, "Inter"), Inter, sans-serif',
                }}
              >
                {/* The clamp rides this span, NOT the <h2> itself. It works on
                    the h2 too (verified in Chrome), but the h2 is a flex item
                    and `-webkit-line-clamp` on a flex item is a long-standing
                    WebKit trouble spot — this repo already hit a clamp leaking
                    an extra line in a nested flex layout (see OpenedItemCard).
                    The bug this fixes was reported from an iOS webview, which
                    we can't test here, so the title gets a plain block child to
                    clamp instead. One node, no rendering difference. */}
                <span
                  ref={titleRef}
                  style={{
                    fontSize: titleFontPx,
                    lineHeight: TITLE_LINE_HEIGHT,
                    // Hard backstop for names too long to fit even at
                    // TITLE_MIN_PX: `useFitText` stops shrinking at the floor,
                    // and without a clamp the surplus lines just spill (a
                    // 95-char card name rendered five and buried the card).
                    // `useFitText` reads this same RESOLVED value back as its
                    // shrink target, so the theme knob moves both together and
                    // the two can never drift.
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: `var(--co-WinnerStepTitle-maxLines, ${TITLE_MAX_LINES})`,
                    overflow: 'hidden',
                  }}
                >
                  {suppressDataDetectors(item.title)}
                </span>
              </h2>
              <span
                aria-hidden
                className="block shrink-0 self-start"
                style={{
                  width: 'var(--co-WinnerStepTitle-exitSpacerWidth, 0px)',
                  height: 'var(--co-WinnerStepTitle-exitSpacerHeight, 52px)',
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Card area — the flex `grow` middle. The card is centred here and
          shrinks on short screens. `headerInstant` paints it immediately on
          post-shuffle episodes (the reel just delivered it) — no re-lift. */}
      {cardSlot === 'scene' && isSleek ? (
        // The scene rests its card in this same frame (useWinnerCardRest), so
        // sleek's chip lands right above it, as over its own card.
        <RevealStage
          visible={visible('image')}
          instant={headerInstant}
          className="relative grow min-h-0 w-full flex items-center justify-center"
        >
          <div className="relative" style={CARD_FRAME_STYLE}>
            <SleekRarityMarks badgeName={rawBadgeName} hasGlow={false} />
          </div>
        </RevealStage>
      ) : cardSlot === 'scene' ? (
        // The opening scene already holds the card here — keep the band so
        // the title and the bottom panel sit exactly where they always do
        <div aria-hidden className="relative grow min-h-0 w-full" />
      ) : (
      <RevealStage
        visible={visible('image')}
        instant={headerInstant}
        className="relative grow min-h-0 w-full flex items-center justify-center"
      >
        <div className="relative" style={CARD_FRAME_STYLE}>
          {/* Sleek (partner Figma 2026-10-08): a rarity chip over the card and
              a soft glow of its colour behind it, for every tier, instead of
              the outlined badge word and the contour comet. */}
          {isSleek && <SleekRarityMarks badgeName={rawBadgeName} />}
          {/* Pedestal — BEHIND the card (z-0), bottom-centred. Kept a touch
              narrower than the scene so it reads as a small plinth (not a
              full-width slab) and sits lower, like the design. */}
          {!isSleek && (
            <img
              src={pedestalSrc}
              alt=""
              aria-hidden
              draggable={false}
              className="pointer-events-none absolute bottom-0 left-1/2 z-0 max-w-none select-none"
              style={{ width: '82%', transform: 'translateX(-50%)' }}
            />
          )}
          {/* Comet tinted to the rarity colour (same source as the scene
              glow / badge), Rare and above only; sleek has the chip instead. */}
          <WinnerCard
            item={item}
            cometColor={!isSleek && isRarePlus(badgeName) ? glowColorFor(badgeName) : null}
          />
        </div>
      </RevealStage>
      )}

      {/* Bottom region — reserved height, bottom-anchored, so swapping
          offer↔resolved never reflows the card above it. Flies DOWN + out
          when the shuffle starts. */}
      <div
        className={
          isSleekBand
            ? 'w-full shrink-0 flex flex-col items-center px-5 pt-5 pb-6'
            : 'w-full shrink-0 flex flex-col items-center justify-end px-6 pb-6'
        }
        style={{ minHeight: reserves.bottom }}
      >
        <AnimatePresence mode="wait">
          {!leaving && (
            <motion.div
              key="winner-bottom"
              className={isSleekBand ? 'w-full flex flex-1 flex-col items-center' : 'w-full flex flex-col items-center'}
              exit={{
                y: 90,
                opacity: 0,
                transition: { duration: 0.4, ease: [0.4, 0, 1, 1] },
              }}
            >
              {isSleek && (
                <RevealStage visible={visible('fmv')} instant={headerInstant} className="w-full">
                  <SleekCardTitle title={item.title} />
                </RevealStage>
              )}
              {demo ? (
                <DemoBottomPanel
                  item={item}
                  nextPackCta={nextPackCta}
                  onTryAgain={onOpenNextPack}
                  onPullForReal={onPullForReal ?? (() => {})}
                  visibleFmv={visible('fmv')}
                  visibleCta={visible('cta')}
                  visibleSecondary={visible('shipping')}
                  fmvRoll={fmvRoll}
                  instant={instant}
                />
              ) : bestOffer ? (
                <BottomPanel
                  item={item}
                  offer={bestOffer}
                  sellState={sellState}
                  onAcceptBuyback={onAcceptBuyback}
                  onOpenNextPack={onOpenNextPack}
                  nextPackCta={nextPackCta}
                  nextPackIsDone={nextPackIsDone}
                  {...(onVault ? { onVault } : {})}
                  {...(onResultShown ? { onResultShown } : {})}
                  onCreateShipping={onCreateShipping}
                  showCreateShipping={showCreateShipping}
                  visibleFmv={visible('fmv')}
                  visibleCta={visible('cta')}
                  visibleShipping={visible('shipping')}
                  fmvRoll={fmvRoll}
                  ctaRoll={ctaRoll}
                  instant={instant}
                />
              ) : isSleek ? (
                <SleekNextPackPanel
                  nextPackCta={nextPackCta}
                  nextPackIsDone={nextPackIsDone}
                  onOpenNextPack={onOpenNextPack}
                  // `on_hands`: the player still holds the card — nothing sold it.
                  mustVault={item.status === 'on_hands'}
                  {...(onVault ? { onVault } : {})}
                  visible={visible('cta')}
                  instant={instant}
                />
              ) : (
                // No active buyback offer — e.g. a returning view where the
                // offer was already accepted. Offer the next pack (Create
                // Shipping stays as a secondary link when still in hand).
                <RevealStage
                  visible={visible('cta')}
                  instant={instant}
                  className="w-full max-w-[480px] mx-auto flex flex-col items-center gap-4"
                >
                  <div className="relative w-full">
                    <BuybackRippleWaves />
                    <Button
                      variant="primary"
                      size="xl"
                      onClick={onOpenNextPack}
                      fullWidth
                      // No gap of its own: the bolt's slot carries the gap and
                      // animates it, so the label glides as the bolt folds
                      className="relative z-10 h-16 gap-0"
                      leftIcon={<SkipBolt />}
                    >
                      {nextPackCta}
                    </Button>
                  </div>
                  {showCreateShipping && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onCreateShipping}
                      className="mx-auto !h-auto !px-0 underline underline-offset-4 type-subhead font-normal"
                    >
                      Create Shipping
                    </Button>
                  )}
                </RevealStage>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
